import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Award } from "lucide-react";

import { api } from "@/lib/api";


export default function CivilizationProfile() {
    const { userId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const d = await api.civilizationPublicProfile(userId);
                setData(d);
            } catch (e) {
                setError(e.status === 404 ? "This profile is private or does not exist." : e.message);
            }
        })();
    }, [userId]);

    if (error) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main grid place-items-center">
                <div data-testid="civ-profile-private" className="max-w-md text-center px-6">
                    <p className="text-2xl font-semibold">Profile unavailable</p>
                    <p className="mt-2 text-sm text-text-secondary">{error}</p>
                </div>
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main">
            <div className="max-w-3xl mx-auto py-16 px-6">
                <div
                    data-testid="civ-profile-card"
                    className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                >
                    <div className="flex items-center gap-4">
                        {data.user.picture ? (
                            <img
                                src={data.user.picture}
                                alt=""
                                className="h-14 w-14 rounded-full object-cover"
                            />
                        ) : (
                            <div className="h-14 w-14 rounded-full bg-[var(--color-panel-ai)] grid place-items-center text-xl font-semibold">
                                {(data.user.name || "?").slice(0, 2).toUpperCase()}
                            </div>
                        )}
                        <div>
                            <h1 className="text-2xl font-semibold">{data.user.name}</h1>
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Tier · {data.tier}
                            </p>
                        </div>
                        <div className="ml-auto text-right">
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                Total score
                            </p>
                            <p className="text-3xl font-semibold tabular-nums">
                                {data.score}
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                    data-testid="civ-profile-badges"
                >
                    <p className="text-[10px] uppercase tracking-widest text-text-secondary flex items-center gap-2">
                        <Award className="h-3 w-3 text-[var(--color-secondary)]" />
                        Badges ({data.badges.length})
                    </p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {data.badges.length === 0 && (
                            <p className="col-span-full text-xs text-text-secondary italic">
                                No badges yet.
                            </p>
                        )}
                        {data.badges.map((b) => {
                            if (!b.badge) return null;
                            const c = {
                                bronze: "#A3A3A3",
                                silver: "#B5B8BF",
                                gold: "#A3A3A3",
                                platinum: "#CCCCCC",
                            }[b.badge.tier] || "#CCCCCC";
                            return (
                                <div
                                    key={b.id}
                                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-3"
                                >
                                    <div
                                        className="inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest"
                                        style={{ color: c, background: `${c}20` }}
                                    >
                                        <Award className="h-3 w-3" /> {b.badge.tier}
                                    </div>
                                    <p className="mt-2 text-sm font-medium">{b.badge.name}</p>
                                    <p className="text-[11px] text-text-secondary">
                                        {b.badge.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
